import { MEETING_KINDS, type FollowedMeetingRow, type FollowedMeetingsResponse, type MeetingKind } from '@pulse/contracts';
import * as React from 'react';

import { PageHeader } from '@/components/pulse/page-header';
import { useChangeFollowedMeetings, useFollowedMeetings, type MeetingChange } from '@/domains/meetings/api';
import { ProjectsListTabs } from '@/domains/projects/projects-list-page';
import { ApiRequestError } from '@/lib/api-client';

const inputStyle: React.CSSProperties = { height: 40, padding: '0 12px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface-2)', color: 'var(--ink)' };

/** Meetings we follow (MT-1), built from the prototype's `viewFollowed()`. */
export function FollowedMeetingsPage(): React.ReactElement {
  const page = useFollowedMeetings();
  const change = useChangeFollowedMeetings();
  const error = change.error instanceof ApiRequestError ? change.error.toUserMessage() : change.error instanceof Error ? change.error.message : null;
  return (
    <>
      <PageHeader eyebrow="Projects · meetings" title="Meetings we follow" sub="After each one, Pulse reads its Notes by Gemini and files what it said under the projects it covers." />
      <ProjectsListTabs view="meetings" />
      {page.data !== undefined ? <FollowedMeetingsView data={page.data} onChange={(next, done) => change.mutate(next, { onSuccess: () => done?.() })} busy={change.isPending} error={error} /> : null}
    </>
  );
}

export function FollowedMeetingsView({
  data,
  onChange,
  busy,
  error,
}: {
  readonly data: FollowedMeetingsResponse;
  readonly onChange: (change: MeetingChange, done?: () => void) => void;
  readonly busy: boolean;
  readonly error: string | null;
}): React.ReactElement {
  const [query, setQuery] = React.useState('');
  const [kind, setKind] = React.useState<MeetingKind>('Weekly review');
  const [projects, setProjects] = React.useState<string[]>([]);
  const [allProjects, setAllProjects] = React.useState(false);
  const [editing, setEditing] = React.useState<string | null>(null);
  return (
    <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px', alignItems: 'start' }}>
      <section className="card pad stack" style={{ gap: 6 }}>
        <h2>Followed</h2>
        {data.meetings.length === 0 ? <span style={{ color: 'var(--muted)' }}>Pulse follows no meeting yet.</span> : null}
        {data.meetings.map((meeting) =>
          editing === meeting.meetingId ? (
            <ChangeMeeting key={meeting.meetingId} meeting={meeting} projects={data.projects} busy={busy} onCancel={() => setEditing(null)} onSave={(body) => onChange({ kind: 'change', meetingId: meeting.meetingId, body }, () => setEditing(null))} />
          ) : (
            <div key={meeting.meetingId} className="prow" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center' }}>
              <span className="stack" style={{ gap: 4 }}>
                <span className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{meeting.title}</span>
                  <span className="chip c-kind">{meeting.kind}</span>
                </span>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  {meeting.allProjects ? 'Covers all projects: Talos files what it said under each project the notes mention' : `Covers ${meeting.projects.map((project) => project.name).join(', ')}`}
                </span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {meeting.schedule} · {meeting.lastNotesLabel === null ? 'no notes read yet' : `last notes ${meeting.lastNotesLabel}`}
                </span>
              </span>
              <span className="row" style={{ gap: 6 }}>
                <button type="button" className="btn" style={{ height: 32 }} onClick={() => setEditing(meeting.meetingId)}>
                  Change
                </button>
                <button type="button" className="btn" style={{ height: 32 }} onClick={() => onChange({ kind: 'stop', meetingId: meeting.meetingId })}>
                  Stop following
                </button>
              </span>
            </div>
          ),
        )}
        <span style={{ fontSize: 13, color: 'var(--muted)', paddingTop: 8 }}>Stopping keeps the summaries already filed.</span>
      </section>
      <section className="card pad stack" style={{ gap: 12 }} aria-labelledby="ffh">
        <h2 id="ffh">Follow a meeting</h2>
        <label className="stack" style={{ gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Meet link or title</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="meet.google.com/abc-defg-hij" style={inputStyle} />
        </label>
        <KindControl kind={kind} onKind={setKind} />
        <ProjectChoices projects={data.projects} chosen={projects} onChosen={setProjects} allProjects={allProjects} onAllProjects={setAllProjects} />
        {error !== null && editing === null ? (
          <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>
            {error}
          </span>
        ) : null}
        <button
          type="button"
          className="btn primary"
          style={{ alignSelf: 'flex-start' }}
          disabled={busy}
          onClick={() =>
            onChange({ kind: 'follow', body: { query, kind, projects: allProjects ? [] : projects, allProjects } }, () => {
              setQuery('');
              setProjects([]);
              setAllProjects(false);
            })
          }
        >
          Follow
        </button>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Pulse finds the meeting in Google Calendar. It needs Notes by Gemini turned on to read anything.</span>
      </section>
    </div>
  );
}

function KindControl({ kind, onKind }: { readonly kind: MeetingKind; readonly onKind: (kind: MeetingKind) => void }): React.ReactElement {
  return (
    <div className="seg" role="group" aria-label="Kind">
      {MEETING_KINDS.map((option) => (
        <button key={option} type="button" aria-pressed={kind === option} onClick={() => onKind(option)}>
          {option}
        </button>
      ))}
    </div>
  );
}

/** The projects a meeting covers, or All projects (MTG-005), which covers every one, later ones too. */
export function ProjectChoices({
  projects,
  chosen,
  onChosen,
  allProjects,
  onAllProjects,
}: {
  readonly projects: FollowedMeetingsResponse['projects'];
  readonly chosen: ReadonlyArray<string>;
  readonly onChosen: (chosen: string[]) => void;
  readonly allProjects: boolean;
  readonly onAllProjects: (allProjects: boolean) => void;
}): React.ReactElement {
  return (
    <fieldset className="stack" style={{ gap: 6, border: 0, padding: 0, margin: 0 }}>
      <legend style={{ fontSize: 13, fontWeight: 500, paddingBottom: 4 }}>Projects it covers</legend>
      <label className="check">
        <input type="checkbox" checked={allProjects} aria-describedby="all-projects-hint" onChange={(event) => onAllProjects(event.target.checked)} />
        All projects
      </label>
      <span id="all-projects-hint" style={{ fontSize: 12, color: 'var(--muted)', marginTop: -4, paddingLeft: 26 }}>
        For a standup or review that touches everything. Talos files each part under the project it&apos;s about.
      </span>
      {projects.map((project) => (
        <label key={project.slug} className="check" style={allProjects ? { opacity: 0.5 } : undefined}>
          <input
            type="checkbox"
            disabled={allProjects}
            checked={!allProjects && chosen.includes(project.slug)}
            onChange={(event) => onChosen(event.target.checked ? [...chosen, project.slug] : chosen.filter((slug) => slug !== project.slug))}
          />
          {project.name}
        </label>
      ))}
    </fieldset>
  );
}

function ChangeMeeting({
  meeting,
  projects,
  busy,
  onSave,
  onCancel,
}: {
  readonly meeting: FollowedMeetingRow;
  readonly projects: FollowedMeetingsResponse['projects'];
  readonly busy: boolean;
  readonly onSave: (body: { kind: MeetingKind; projects: string[]; allProjects: boolean }) => void;
  readonly onCancel: () => void;
}): React.ReactElement {
  const [kind, setKind] = React.useState<MeetingKind>(meeting.kind);
  const [chosen, setChosen] = React.useState<string[]>(meeting.projects.map((project) => project.slug));
  const [allProjects, setAllProjects] = React.useState(meeting.allProjects);
  return (
    <div className="stack" style={{ gap: 10, padding: '12px 0', borderTop: '1px solid var(--line-2)' }}>
      <span style={{ fontWeight: 600 }}>{meeting.title}</span>
      <KindControl kind={kind} onKind={setKind} />
      <ProjectChoices projects={projects} chosen={chosen} onChosen={setChosen} allProjects={allProjects} onAllProjects={setAllProjects} />
      <span className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary" disabled={busy} onClick={() => onSave({ kind, projects: allProjects ? [] : chosen, allProjects })}>
          Save
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </span>
    </div>
  );
}
